import React, { useState, useEffect, useContext } from 'react';
import {
  Typography, Card, Row, Col, Form, Input, InputNumber, Select, Button, Space,
  Breadcrumb, Alert, Divider, Tag, message, Popconfirm, Checkbox, Tooltip,
} from 'antd';
import {
  ArrowUpOutlined, ArrowDownOutlined, DeleteOutlined, PlusOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { UserContext } from '../App';
import { usePageLabel } from '../components/BackChip';

const uid = () => Math.random().toString(36).slice(2);

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function OptionRow({ option, onChange, onDelete, readOnly, noPoints }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
      <Input
        placeholder="Option text…"
        value={option.text}
        onChange={(e) => onChange({ text: e.target.value })}
        size="small"
        disabled={readOnly}
        style={{ flex: 1 }}
      />
      {!noPoints && (
        <InputNumber
          min={0}
          max={100}
          value={option.points}
          onChange={(v) => onChange({ points: v ?? 0 })}
          addonAfter="pts"
          size="small"
          disabled={readOnly}
          style={{ width: 115 }}
        />
      )}
      {!readOnly && (
        <Button size="small" danger icon={<DeleteOutlined />} onClick={onDelete} />
      )}
    </div>
  );
}

// Score thresholds have moved to CampaignGoals, where a target is scoped to a
// campaign and bounded by StartMonth/EndMonth. A form-level target could do
// neither: one form serves several campaigns, and editing the number silently
// rewrote what every past score meant.
//
// The card is hidden rather than removed, and the values are still loaded and
// saved untouched, so nothing is lost while the analytics pages are migrated
// off EvalForms.ScoreTarget. Flip this back to true to expose it again.
const SHOW_FORM_SCORE_THRESHOLDS = false;

function QuestionCard({ question, onUpdate, onDelete, onMove, isFirst, isLast, readOnly, textEditOnly, formType }) {
  const addOption = () =>
    onUpdate({ options: [...question.options, { _key: uid(), text: '', points: 0 }] });

  const updateOption = (okey, patch) =>
    onUpdate({ options: question.options.map((o) => (o._key === okey ? { ...o, ...patch } : o)) });

  const deleteOption = (okey) =>
    onUpdate({ options: question.options.filter((o) => o._key !== okey) });

  const moveOption = (okey, dir) => {
    const arr = [...question.options];
    const i = arr.findIndex((o) => o._key === okey);
    if (i + dir < 0 || i + dir >= arr.length) return;
    [arr[i], arr[i + dir]] = [arr[i + dir], arr[i]];
    onUpdate({ options: arr });
  };

  return (
    <Card
      size="small"
      style={{ marginBottom: 10, background: '#fafafa' }}
      bodyStyle={{ padding: '10px 12px' }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          {/* Type + max points + mandatory row */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Select
              value={question.type}
              onChange={(v) => onUpdate({ type: v, options: [], max_points: 0 })}
              options={[
                ...(formType !== 'error_tracking' ? [
                  { label: 'Input (score + notes)', value: 'input' },
                  { label: 'Pick List', value: 'picklist' },
                ] : []),
                { label: 'Text field (no points)', value: 'text' },
                { label: 'Date field (no points)', value: 'date' },
                { label: 'Info Dropdown (no score)', value: 'info_select' },
                { label: 'Error Count (0–1)', value: 'error_count' },
              ]}
              size="small"
              disabled={readOnly || textEditOnly}
              style={{ width: 210 }}
            />
            {question.type === 'input' && (
              <InputNumber
                min={0}
                max={100}
                value={question.max_points}
                onChange={(v) => onUpdate({ max_points: v ?? 0 })}
                addonAfter="max pts"
                size="small"
                disabled={readOnly || textEditOnly}
                style={{ width: 140 }}
              />
            )}
            {question.type === 'picklist' && question.options.length > 0 && (
              <Tag>max {Math.max(0, ...question.options.map((o) => o.points))} pts</Tag>
            )}
            {(question.type === 'text' || question.type === 'date' || question.type === 'info_select') && (
              <Tag color="default">no points</Tag>
            )}
            {question.type === 'error_count' && (
              <Tag color="orange">0–1 count</Tag>
            )}
            <Checkbox
              checked={question.mandatory}
              onChange={(e) => onUpdate({ mandatory: e.target.checked })}
              disabled={readOnly || textEditOnly}
            >
              Mandatory
            </Checkbox>
            {/* Evaluation forms only. One per form: its answer shows on hover
                in the evaluation search. Settable on a published form (it
                never changes scoring). */}
            {formType === 'evaluation' && (
              <Checkbox
                checked={!!question.is_feedback}
                onChange={(e) => onUpdate({ is_feedback: e.target.checked })}
                disabled={readOnly}
              >
                Show as Feedback in search results
              </Checkbox>
            )}
          </div>

          {/* Question text */}
          <Input.TextArea
            placeholder="Question text…"
            value={question.text}
            onChange={(e) => onUpdate({ text: e.target.value })}
            autoSize={{ minRows: 1, maxRows: 3 }}
            disabled={readOnly}
          />

          {/* Guidance */}
          <Input.TextArea
            placeholder="Guidance (optional) — displayed to the QA reviewer as a hint…"
            value={question.guidance || ''}
            onChange={(e) => onUpdate({ guidance: e.target.value })}
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={readOnly}
            style={{ marginTop: 6, fontSize: 12, color: '#595959' }}
          />

          {/* AI instructions. Never rendered anywhere but here -- not on the
              evaluation, not to the agent. It is sent to the model with the
              guidance above, and the two are separate because their readers
              are: guidance tells a person what to look for, this says how to
              read a transcript.

              Evaluation forms only: an error tracking or helpdesk form has no
              call for the AI to score, so the field would be dead space. */}
          {formType === 'evaluation' && (
            <>
              <Input.TextArea
                placeholder="AI additional instructions (optional) — sent to the AI only, never displayed. Type DONOTSCORE to exclude this question from AI scoring."
                value={question.ai_instructions || ''}
                onChange={(e) => onUpdate({ ai_instructions: e.target.value })}
                autoSize={{ minRows: 1, maxRows: 4 }}
                disabled={readOnly}
                style={{ marginTop: 6, fontSize: 12, color: '#595959', background: '#fcfcff' }}
              />
              {(question.ai_instructions || '').toUpperCase().includes('DONOTSCORE') && (
                <Tag color="purple" style={{ marginTop: 4 }}>Excluded from AI scoring</Tag>
              )}
            </>
          )}


          {/* Pick list options */}
          {(question.type === 'picklist' || question.type === 'info_select') && (
            <div style={{ marginTop: 10 }}>
              {question.options.map((opt, oi) => (
                <div key={opt._key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ flex: 1 }}>
                    <OptionRow
                      option={opt}
                      onChange={(patch) => updateOption(opt._key, patch)}
                      onDelete={() => deleteOption(opt._key)}
                      readOnly={readOnly || textEditOnly}
                      noPoints={question.type === 'info_select'}
                    />
                  </div>
                  {!readOnly && !textEditOnly && (
                    <Space size={2} style={{ marginBottom: 6 }}>
                      <Button
                        size="small"
                        icon={<ArrowUpOutlined />}
                        onClick={() => moveOption(opt._key, -1)}
                        disabled={oi === 0}
                      />
                      <Button
                        size="small"
                        icon={<ArrowDownOutlined />}
                        onClick={() => moveOption(opt._key, 1)}
                        disabled={oi === question.options.length - 1}
                      />
                    </Space>
                  )}
                </div>
              ))}
              {!readOnly && !textEditOnly && (
                <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={addOption} style={{ marginTop: 4 }}>
                  Add Option
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Question-level controls */}
        {!readOnly && !textEditOnly && (
          <Space direction="vertical" size={4}>
            <Button size="small" icon={<ArrowUpOutlined />} onClick={() => onMove(-1)} disabled={isFirst} />
            <Button size="small" icon={<ArrowDownOutlined />} onClick={() => onMove(1)} disabled={isLast} />
            <Popconfirm title="Delete this question?" onConfirm={onDelete} okButtonProps={{ danger: true }}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        )}
      </div>
    </Card>
  );
}

function SectionCard({ section, onUpdate, onDelete, onMove, isFirst, isLast, readOnly, textEditOnly, formType }) {
  const addQuestion = () =>
    onUpdate({
      questions: [
        ...section.questions,
        { _key: uid(), text: '', type: 'input', max_points: 0, guidance: '', ai_instructions: '', mandatory: true, options: [] },
      ],
    });

  const updateQuestion = (qkey, patch) =>
    onUpdate({
      questions: section.questions.map((q) => (q._key === qkey ? { ...q, ...patch } : q)),
    });

  const deleteQuestion = (qkey) =>
    onUpdate({ questions: section.questions.filter((q) => q._key !== qkey) });

  const moveQuestion = (qkey, dir) => {
    const arr = [...section.questions];
    const i = arr.findIndex((q) => q._key === qkey);
    if (i + dir < 0 || i + dir >= arr.length) return;
    [arr[i], arr[i + dir]] = [arr[i + dir], arr[i]];
    onUpdate({ questions: arr });
  };

  const maxPts = section.questions.reduce((sum, q) => {
    if (q.type === 'picklist') return sum + Math.max(0, ...q.options.map((o) => o.points), 0);
    return sum + (q.max_points || 0);
  }, 0);

  return (
    <Card
      style={{ marginBottom: 16 }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {readOnly && !textEditOnly ? (
            <Typography.Text strong>{section.name}</Typography.Text>
          ) : (
            <Input
              value={section.name}
              onChange={(e) => onUpdate({ name: e.target.value })}
              style={{ fontWeight: 600, maxWidth: 300 }}
              bordered={false}
              disabled={readOnly}
            />
          )}
          {maxPts > 0 && <Tag color="blue">{maxPts} pts</Tag>}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {section.questions.length} question{section.questions.length !== 1 ? 's' : ''}
          </Typography.Text>
        </div>
      }
      extra={
        !readOnly && !textEditOnly && (
          <Space>
            <Button size="small" icon={<ArrowUpOutlined />} onClick={() => onMove(-1)} disabled={isFirst} />
            <Button size="small" icon={<ArrowDownOutlined />} onClick={() => onMove(1)} disabled={isLast} />
            <Popconfirm
              title="Delete this section and all its questions?"
              onConfirm={onDelete}
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        )
      }
    >
      {section.questions.map((q, qi) => (
        <QuestionCard
          key={q._key}
          question={q}
          onUpdate={(patch) => updateQuestion(q._key, patch)}
          onDelete={() => deleteQuestion(q._key)}
          onMove={(dir) => moveQuestion(q._key, dir)}
          isFirst={qi === 0}
          isLast={qi === section.questions.length - 1}
          readOnly={readOnly}
          textEditOnly={textEditOnly}
          formType={formType}
        />
      ))}
      {!readOnly && !textEditOnly && (
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addQuestion}
          size="small"
          style={{ width: '100%', marginTop: section.questions.length > 0 ? 4 : 0 }}
        >
          Add Question
        </Button>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function serverToLocal(f) {
  return {
    name: f.Name || '',
    description: f.Description || '',
    score_min:    f.ScoreMin    ?? null,
    score_target: f.ScoreTarget ?? null,
    ai_score_mode: f.AIScoreMode ?? 0,
    score_high:   f.ScoreHighAchiever ?? null,
    dispute_window_days: f.DisputeWindowDays ?? 4,
    disable_dispute: !!f.DisableDispute,
    allow_no_call: !!f.AllowNoCall,
    max_one_error_per_section: f.MaxOneErrorPerSection !== 0,
    sections: (f.Sections || []).map((s) => ({
      _key: uid(),
      _id: s.ID,
      name: s.Name,
      questions: (s.Questions || []).map((q) => ({
        _key: uid(),
        _id: q.ID,
        text: q.QuestionText,
        type: q.QuestionType,
        max_points: q.MaxPoints,
        guidance: q.Guidance || '',
        ai_instructions: q.AIInstructions || '',
        mandatory: q.IsMandatory !== 0,
        is_feedback: !!q.IsFeedback,
        options: (q.Options || []).map((o) => ({
          _key: uid(),
          text: o.OptionText,
          points: o.Points,
        })),
      })),
    })),
  };
}

function buildTextBody(form) {
  const sections = [];
  const questions = [];
  for (const s of form.sections) {
    if (s._id) sections.push({ id: s._id, name: s.name });
    for (const q of s.questions) {
      // Editable on a published form for the same reason guidance is: it
      // changes how a question is read, never what it is worth.
      if (q._id) questions.push({ id: q._id, text: q.text, guidance: q.guidance || '', ai_instructions: q.ai_instructions || '', is_feedback: !!q.is_feedback });
    }
  }
  return {
    sections,
    questions,
    score_min:    form.score_min    ?? null,
    score_target: form.score_target ?? null,
    ai_score_mode: form.ai_score_mode ?? 0,
    score_high:   form.score_high   ?? null,
    dispute_window_days:        form.dispute_window_days ?? 4,
    disable_dispute:            !!form.disable_dispute,
    allow_no_call:              !!form.allow_no_call,
    max_one_error_per_section:  form.max_one_error_per_section !== false,
  };
}

function buildBody(form) {
  return {
    name: form.name,
    description: form.description,
    score_min:    form.score_min    ?? null,
    score_target: form.score_target ?? null,
    ai_score_mode: form.ai_score_mode ?? 0,
    score_high:   form.score_high   ?? null,
    dispute_window_days: form.dispute_window_days ?? 4,
    disable_dispute: !!form.disable_dispute,
    allow_no_call: !!form.allow_no_call,
    max_one_error_per_section: form.max_one_error_per_section !== false,
    sections: form.sections.map((s) => ({
      name: s.name,
      questions: s.questions.map((q) => ({
        text: q.text,
        type: q.type,
        guidance: q.guidance || '',
        ai_instructions: q.ai_instructions || '',
        mandatory: q.mandatory !== false,
        is_feedback: !!q.is_feedback,
        max_points:
          q.type === 'picklist'
            ? Math.max(0, ...q.options.map((o) => o.points), 0)
            : q.max_points || 0,
        options: q.options.map((o) => ({ text: o.text, points: o.points })),
      })),
    })),
  };
}

export default function EvalFormEditorPage() {
  const { formId } = useParams();
  const navigate   = useNavigate();
  const currentUser = useContext(UserContext);
  const canEdit     = (currentUser?.CanConfigEvals ?? 0) >= 2;
  const [formState, setFormState] = useState({ name: '', description: '', score_min: null, score_target: null, score_high: null, ai_score_mode: 0, dispute_window_days: 4, disable_dispute: false, allow_no_call: false, max_one_error_per_section: true, sections: [] });
  // Names this page in Recent and on the next page's Back chip.
  usePageLabel(formState.name ? `Eval Form: ${formState.name}` : null);
  const [formType, setFormType] = useState('evaluation');
  const [status, setStatus] = useState('draft');
  const [isVersioned, setIsVersioned] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingText, setSavingText] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [campaignIds,  setCampaignIds]  = useState([]);
  const [allTeams,              setAllTeams]              = useState([]);
  const [allQueues,             setAllQueues]             = useState([]);
  const [queueTeamId,           setQueueTeamId]           = useState(null);
  const [slaFirstResponseHours, setSlaFirstResponseHours] = useState(null);
  const [slaResolutionHours,    setSlaResolutionHours]    = useState(null);
  // Help desk: who may submit (empty = anyone).
  const [submitterIds,          setSubmitterIds]          = useState([]);   // Restricted Impacted Employees
  const [creatorIds,            setCreatorIds]            = useState([]);   // Can Create
  // Help desk: the queue's task checklist (never shown to the submitter).
  const [helpDeskTasks,         setHelpDeskTasks]         = useState([]);
  // Help desk: self-service solutions offered before a ticket is created,
  // and how often each has fixed it (this form version).
  const [helpDeskSolutions,     setHelpDeskSolutions]     = useState([]);
  const [solutionStats,         setSolutionStats]         = useState(null);
  // Help desk: tickets are about an Applicable Employee, who never sees them.
  const [usesApplicable,        setUsesApplicable]        = useState(false);
  const [isClientTicket,        setIsClientTicket]        = useState(false);
  const [queueOnlyClose,        setQueueOnlyClose]        = useState(false);
  // Help desk: close tickets left Waiting on Customer this many days (blank = never).
  const [autoCloseDays,         setAutoCloseDays]         = useState(null);
  const [allUsers,              setAllUsers]              = useState([]);

  const isNew = formId === 'new';

  useEffect(() => {
    api.get('/api/admin/campaigns')
      .then((r) => setAllCampaigns(r.data))
      .catch(() => {});
    api.get('/api/users')
      .then((r) => setAllUsers(r.data || []))
      .catch(() => {});
    api.get('/api/helpdesk/queues')
      .then((r) => setAllQueues(r.data || []))
      .catch(() => {});
    api.get('/api/teams')
      .then((r) => setAllTeams(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (isNew) return;
    api
      .get(`/api/eval-forms/${formId}`)
      .then((res) => {
        setStatus(res.data.Status);
        setIsVersioned(!!res.data.ParentFormID);
        setFormType(res.data.form_type || 'evaluation');
        setFormState(serverToLocal(res.data));
        setCampaignIds((res.data.Campaigns || []).map((c) => c.ID));
        setQueueTeamId(res.data.HelpDeskQueueID || null);
        setSlaFirstResponseHours(res.data.SLAFirstResponseHours || null);
        setSlaResolutionHours(res.data.SLAResolutionHours || null);
        setSubmitterIds(res.data.HelpDeskSubmitterIDs || []);
        setCreatorIds(res.data.HelpDeskCreatorIDs || []);
        setHelpDeskTasks(res.data.HelpDeskTasks || []);
        setHelpDeskSolutions((res.data.HelpDeskSolutions || []).map((x) => ({ id: x.ID, title: x.Title || '', steps: x.Steps || '', url: x.URL || '',
          show_on_create: x.ShowOnCreate == null ? true : !!x.ShowOnCreate })));
        if (res.data.form_type === 'helpdesk') {
          api.get(`/api/helpdesk/forms/${formId}/self-service-stats`).then((r) => setSolutionStats(r.data)).catch(() => {});
        }
        setUsesApplicable(!!res.data.HelpDeskApplicableEmployee);
        setIsClientTicket(!!res.data.HelpDeskClientTicket);
        setQueueOnlyClose(!!res.data.HelpDeskQueueOnlyClose);
        setAutoCloseDays(res.data.HelpDeskAutoCloseDays || null);
      })
      .catch(() => message.error('Failed to load form.'));
  }, [formId, isNew]);

  const textEditOnly = canEdit && status === 'published';
  const readOnly     = !canEdit || status === 'inactive';

  const totalMaxPts = formState.sections.reduce((sum, s) =>
    sum + s.questions.reduce((qs, q) =>
      qs + (q.type === 'picklist'
        ? Math.max(0, ...q.options.map((o) => o.points), 0)
        : q.max_points || 0), 0), 0);

  const updateSection = (skey, patch) =>
    setFormState((f) => {
      let sections = f.sections.map((s) => (s._key === skey ? { ...s, ...patch } : s));
      // Feedback is one question per form: ticking it on one clears the rest.
      const before = new Set(f.sections.flatMap((s) => s.questions.filter((q) => q.is_feedback).map((q) => q._key)));
      const newlyOn = (patch.questions || []).find((q) => q.is_feedback && !before.has(q._key));
      if (newlyOn) {
        sections = sections.map((s) => ({
          ...s,
          questions: s.questions.map((q) => (q._key !== newlyOn._key && q.is_feedback ? { ...q, is_feedback: false } : q)),
        }));
      }
      return { ...f, sections };
    });

  const deleteSection = (skey) =>
    setFormState((f) => ({ ...f, sections: f.sections.filter((s) => s._key !== skey) }));

  const moveSection = (skey, dir) =>
    setFormState((f) => {
      const arr = [...f.sections];
      const i = arr.findIndex((s) => s._key === skey);
      if (i + dir < 0 || i + dir >= arr.length) return f;
      [arr[i], arr[i + dir]] = [arr[i + dir], arr[i]];
      return { ...f, sections: arr };
    });

  const addSection = () =>
    setFormState((f) => ({
      ...f,
      sections: [
        ...f.sections,
        { _key: uid(), name: 'New Section', questions: [] },
      ],
    }));

  const validateScores = () => {
    // Only meaningful while the thresholds are editable. With the card hidden a
    // stored threshold above the form total is unfixable from the UI, so this
    // would block the save with an error about a field that is not on screen --
    // reachable just by deleting a question.
    if (!SHOW_FORM_SCORE_THRESHOLDS) return true;
    const { score_min, score_target, score_high } = formState;
    const over = [
      score_min    != null && score_min    > totalMaxPts && `Silver (${score_min})`,
      score_target != null && score_target > totalMaxPts && `Target (Gold) (${score_target})`,
      score_high   != null && score_high   > totalMaxPts && `Platinum (${score_high})`,
    ].filter(Boolean);
    if (over.length) {
      message.error(`${over.join(', ')} exceed${over.length === 1 ? 's' : ''} the form total of ${totalMaxPts} pts.`);
      return false;
    }
    return true;
  };

  const doSave = async (targetId) => {
    if (!formState.name.trim()) {
      message.error('Form name is required.');
      return null;
    }
    if (!validateScores()) return null;
    await api.put(`/api/eval-forms/${targetId}`, {
      ...buildBody(formState),
      campaign_ids: campaignIds,
      help_desk_queue_id: queueTeamId,
      sla_first_response_hours: slaFirstResponseHours,
      sla_resolution_hours: slaResolutionHours,
      help_desk_submitter_ids: submitterIds,
      help_desk_creator_ids: creatorIds,
      help_desk_tasks:         helpDeskTasks,
      help_desk_solutions:     helpDeskSolutions,
      help_desk_applicable_employee: usesApplicable,
      help_desk_client_ticket: isClientTicket,
      help_desk_queue_only_close: queueOnlyClose,
      help_desk_auto_close_days:     autoCloseDays,
    });
    return targetId;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const res = await api.post('/api/eval-forms', {
          name: formState.name || 'New Evaluation Form',
          description: formState.description,
        });
        const newId = res.data.id;
        await doSave(newId);
        navigate(`/eval-forms/${newId}/edit`, { replace: true });
      } else {
        await doSave(formId);
      }
      message.success('Draft saved.');
    } catch {
      message.error('Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveText = async () => {
    if (!validateScores()) return;
    setSavingText(true);
    try {
      await api.patch(`/api/eval-forms/${formId}/text`, {
        ...buildTextBody(formState),
        campaign_ids:             campaignIds,
        help_desk_queue_id:       queueTeamId,
        sla_first_response_hours: slaFirstResponseHours,
        sla_resolution_hours:     slaResolutionHours,
        help_desk_submitter_ids:  submitterIds,
        help_desk_creator_ids:    creatorIds,
        help_desk_tasks:          helpDeskTasks,
        help_desk_solutions:      helpDeskSolutions,
        help_desk_applicable_employee: usesApplicable,
        help_desk_client_ticket: isClientTicket,
        help_desk_queue_only_close: queueOnlyClose,
        help_desk_auto_close_days:     autoCloseDays,
      });
      message.success('Text changes saved.');
    } catch {
      message.error('Failed to save text changes.');
    } finally {
      setSavingText(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      // Save first
      let id = formId;
      if (isNew) {
        const res = await api.post('/api/eval-forms', {
          name: formState.name || 'New Evaluation Form',
          description: formState.description,
        });
        id = res.data.id;
      }
      await doSave(id);
      await api.post(`/api/eval-forms/${id}/publish`);
      setStatus('published');
      if (isNew) navigate(`/eval-forms/${id}/edit`, { replace: true });
      message.success('Form published!');
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to publish.');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <Breadcrumb
        items={[
          { title: <a onClick={() => navigate('/eval-forms')}>Evaluation Forms</a> },
          { title: formState.name || 'New Form' },
        ]}
        style={{ marginBottom: 16 }}
      />

      {textEditOnly && (
        <Alert
          type="info"
          message='This form is published. You can update section names, question text, and guidance — but not scores, types, or structure. Use "New Version" to make structural changes.'
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}
      {readOnly && (
        <Alert
          type="warning"
          message='This form is inactive and cannot be edited. Use "New Version" from the forms list to create an editable copy.'
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      {/* Basic info */}
      <Card
        style={{ marginBottom: 16 }}
        extra={
          totalMaxPts > 0 && (
            <Tag color="blue" style={{ fontSize: 13 }}>{totalMaxPts} total pts</Tag>
          )
        }
      >
        <Form layout="vertical">
          <Form.Item
            label="Form Name"
            required
            style={{ marginBottom: 12 }}
            extra={isVersioned ? 'Form name is shared across all versions and cannot be changed here.' : undefined}
          >
            <Input
              value={formState.name}
              onChange={(e) => setFormState((f) => ({ ...f, name: e.target.value }))}
              disabled={readOnly || textEditOnly || isVersioned}
              placeholder="e.g. Standard QA Evaluation"
            />
          </Form.Item>
          <Form.Item label="Description" style={{ marginBottom: 12 }}>
            <Input.TextArea
              value={formState.description}
              onChange={(e) => setFormState((f) => ({ ...f, description: e.target.value }))}
              disabled={readOnly || textEditOnly}
              autoSize={{ minRows: 2, maxRows: 4 }}
              placeholder="Optional description…"
            />
          </Form.Item>
          {/* Not shown for help desk forms: campaigns do not apply to them. */}
          {formType !== 'helpdesk' && (
          <Form.Item
            label="Campaigns"
            style={{ marginBottom: 0 }}
            extra="Leave empty to show this form for all campaigns. Select one or more to restrict it to those campaigns only."
          >
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              allowClear
              disabled={readOnly}
              placeholder="All campaigns (unrestricted)…"
              value={campaignIds}
              onChange={setCampaignIds}
              options={allCampaigns.filter((c) => c.IsActive).map((c) => ({
                label: c.CXCampaignNo ? `${c.Name} (${c.CXCampaignNo})` : c.Name,
                value: c.ID,
              }))}
              style={{ width: '100%' }}
            />
          </Form.Item>
          )}
          {formType === 'helpdesk' && (
            <>
              <Form.Item
                label="Queue"
                style={{ marginBottom: 12 }}
                extra="The help desk queue that works tickets submitted with this form."
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select queue…"
                  value={queueTeamId}
                  onChange={(v) => setQueueTeamId(v ?? null)}
                  options={allQueues
                    .filter((q) => q.IsActive || q.ID === queueTeamId)
                    .map((q) => ({ label: q.IsActive ? q.Name : `${q.Name} (inactive)`, value: q.ID }))}
                  disabled={readOnly}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label="SLA First Response" style={{ marginBottom: 0 }}
                    extra="Hours from creation to first response target.">
                    <InputNumber
                      min={1}
                      value={slaFirstResponseHours}
                      onChange={(v) => setSlaFirstResponseHours(v ?? null)}
                      addonAfter="hrs"
                      style={{ width: '100%' }}
                      disabled={readOnly}
                      placeholder="—"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="SLA Resolution" style={{ marginBottom: 0 }}
                    extra="Hours from creation to resolution target.">
                    <InputNumber
                      min={1}
                      value={slaResolutionHours}
                      onChange={(v) => setSlaResolutionHours(v ?? null)}
                      addonAfter="hrs"
                      style={{ width: '100%' }}
                      disabled={readOnly}
                      placeholder="—"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Can Create" style={{ marginTop: 12, marginBottom: 0 }}>
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Anyone (leave empty)"
                  value={creatorIds}
                  onChange={setCreatorIds}
                  disabled={readOnly}
                  options={allUsers.filter((u) => u.IsActive !== 0 || creatorIds.includes(u.ID))
                    .map((u) => ({ value: u.ID, label: u.TeamName ? `${u.DisplayName} (${u.TeamName})` : u.DisplayName }))}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="Restricted Impacted Employees" style={{ marginTop: 12, marginBottom: 0 }}>
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Not restricted (leave empty)"
                  value={submitterIds}
                  onChange={setSubmitterIds}
                  disabled={readOnly}
                  options={allUsers.filter((u) => u.IsActive !== 0 || submitterIds.includes(u.ID))
                    .map((u) => ({ value: u.ID, label: u.TeamName ? `${u.DisplayName} (${u.TeamName})` : u.DisplayName }))}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item style={{ marginTop: 12, marginBottom: 0 }}>
                <Checkbox checked={usesApplicable} disabled={readOnly} onChange={(e) => setUsesApplicable(e.target.checked)}>
                  Uses an Applicable Employee (they cannot see the ticket)
                </Checkbox>
              </Form.Item>
              <Form.Item style={{ marginTop: 4, marginBottom: 0 }}>
                <Checkbox checked={isClientTicket} disabled={readOnly} onChange={(e) => setIsClientTicket(e.target.checked)}>
                  Client Ticket Type
                </Checkbox>
              </Form.Item>
              <Form.Item style={{ marginTop: 4, marginBottom: 0 }}>
                <Checkbox checked={queueOnlyClose} disabled={readOnly} onChange={(e) => setQueueOnlyClose(e.target.checked)}>
                  Only the queue can close it (the Impacted Employee cannot)
                </Checkbox>
              </Form.Item>
              <Form.Item label="Auto-close after (days Waiting on Customer)" style={{ marginTop: 12, marginBottom: 0 }}>
                <InputNumber
                  min={1} precision={0} placeholder="Never" disabled={readOnly}
                  value={autoCloseDays} onChange={(v) => setAutoCloseDays(v || null)}
                  addonAfter="days" style={{ width: 200 }}
                />
              </Form.Item>
              <Form.Item label="Queue Tasks" style={{ marginTop: 12, marginBottom: 0 }}>
                {helpDeskTasks.map((t, i) => (
                  <Space.Compact key={i} style={{ width: '100%', marginBottom: 6 }}>
                    <Input
                      value={t}
                      maxLength={200}
                      disabled={readOnly}
                      onChange={(e) => setHelpDeskTasks(helpDeskTasks.map((x, j) => (j === i ? e.target.value : x)))}
                    />
                    <Button icon={<ArrowUpOutlined />} disabled={readOnly || i === 0}
                      onClick={() => { const a = [...helpDeskTasks]; [a[i - 1], a[i]] = [a[i], a[i - 1]]; setHelpDeskTasks(a); }} />
                    <Button icon={<ArrowDownOutlined />} disabled={readOnly || i === helpDeskTasks.length - 1}
                      onClick={() => { const a = [...helpDeskTasks]; [a[i + 1], a[i]] = [a[i], a[i + 1]]; setHelpDeskTasks(a); }} />
                    <Button danger icon={<DeleteOutlined />} disabled={readOnly}
                      onClick={() => setHelpDeskTasks(helpDeskTasks.filter((_, j) => j !== i))} />
                  </Space.Compact>
                ))}
                {!readOnly && (
                  <Button size="small" icon={<PlusOutlined />} onClick={() => setHelpDeskTasks([...helpDeskTasks, ''])}>
                    Add Task
                  </Button>
                )}
              </Form.Item>
              {/* Offered on the create popup before the ticket form; skippable. */}
              <Form.Item label="Self-Service Solutions" style={{ marginTop: 12, marginBottom: 0 }}>
                {solutionStats && (solutionStats.solved_total > 0 || solutionStats.tickets_after > 0) && (
                  <div style={{ fontSize: 12, color: '#595959', marginBottom: 8 }}>
                    Fixed without a ticket: <strong>{solutionStats.solved_total}</strong>
                    {' · '}Tickets created after seeing these: <strong>{solutionStats.tickets_after}</strong>
                  </div>
                )}
                {helpDeskSolutions.map((sol, i) => {
                  const set = (patch) => setHelpDeskSolutions(helpDeskSolutions.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                  const fixed = sol.id && solutionStats?.solved?.[String(sol.id)];
                  return (
                    <div key={sol.id || `new-${i}`} style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 8, marginBottom: 8 }}>
                      <Space.Compact style={{ width: '100%', marginBottom: 6 }}>
                        <Input value={sol.title} maxLength={200} disabled={readOnly} placeholder="Title"
                          onChange={(e) => set({ title: e.target.value })} />
                        <Button icon={<ArrowUpOutlined />} disabled={readOnly || i === 0}
                          onClick={() => { const a = [...helpDeskSolutions]; [a[i - 1], a[i]] = [a[i], a[i - 1]]; setHelpDeskSolutions(a); }} />
                        <Button icon={<ArrowDownOutlined />} disabled={readOnly || i === helpDeskSolutions.length - 1}
                          onClick={() => { const a = [...helpDeskSolutions]; [a[i + 1], a[i]] = [a[i], a[i + 1]]; setHelpDeskSolutions(a); }} />
                        <Button danger icon={<DeleteOutlined />} disabled={readOnly}
                          onClick={() => setHelpDeskSolutions(helpDeskSolutions.filter((_, j) => j !== i))} />
                      </Space.Compact>
                      <Input.TextArea value={sol.steps} disabled={readOnly} placeholder="Steps (one per line)"
                        autoSize={{ minRows: 2, maxRows: 8 }} style={{ marginBottom: 6 }}
                        onChange={(e) => set({ steps: e.target.value })} />
                      <Input value={sol.url} disabled={readOnly} placeholder="Link (optional)" maxLength={2000}
                        onChange={(e) => set({ url: e.target.value })} />
                      {/* Off: not offered on New Ticket, but the queue can still insert it into a comment. */}
                      <Checkbox checked={sol.show_on_create !== false} disabled={readOnly} style={{ marginTop: 6 }}
                        onChange={(e) => set({ show_on_create: e.target.checked })}>
                        Show on Create Page
                      </Checkbox>
                      {fixed ? <div style={{ fontSize: 12, color: '#52c41a', marginTop: 4 }}>Fixed it {fixed} time{fixed === 1 ? '' : 's'}</div> : null}
                    </div>
                  );
                })}
                {!readOnly && (
                  <Button size="small" icon={<PlusOutlined />}
                    onClick={() => setHelpDeskSolutions([...helpDeskSolutions, { title: '', steps: '', url: '', show_on_create: true }])}>
                    Add Solution
                  </Button>
                )}
              </Form.Item>
            </>
          )}
        </Form>
      </Card>

      {/* Dispute / Rescore Window — not applicable for help desk forms */}
      {/* AI scoring. Its own card rather than a row inside the score
          thresholds one, which is switched off by SHOW_FORM_SCORE_THRESHOLDS
          and takes anything placed in it off the screen with it. */}
      {formType === 'evaluation' && <Card title="AI Scoring" size="small" style={{ marginBottom: 16 }}>
        <Form layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<Tooltip title="Whether the AI drafts a scorecard from the call transcript for contacts on this form. Feedback Only creates coaching material outside the agent's score; Evaluation creates a scored evaluation that counts like any other.">Have AI Score Contact <InfoCircleOutlined style={{ fontSize: 11, opacity: 0.45 }} /></Tooltip>}
                style={{ marginBottom: 0 }}
              >
                <Select
                  value={formState.ai_score_mode ?? 0}
                  onChange={(v) => setFormState((f) => ({ ...f, ai_score_mode: v }))}
                  disabled={readOnly}
                  style={{ width: '100%' }}
                  options={[
                    { value: 0, label: 'No' },
                    { value: 1, label: 'Feedback Only' },
                    { value: 2, label: 'Evaluation' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>}
      {/* Allow without a call: the form can be used for an evaluation that is
          not tied to a call (New Evaluation on the Evaluations page), for a
          client whose CCaaS system we do not use. Only on the form's own
          campaigns, so a form with none cannot be used that way. */}
      {formType === 'evaluation' && <Card title="Call Requirement" size="small" style={{ marginBottom: 16 }}>
        <Checkbox
          checked={!!formState.allow_no_call}
          onChange={(e) => setFormState((f) => ({ ...f, allow_no_call: e.target.checked }))}
          disabled={readOnly}
        >
          Allow without a call
        </Checkbox>
      </Card>}
      {formType !== 'helpdesk' && <Card title="Dispute Settings" size="small" style={{ marginBottom: 16 }}>
        <Form layout="vertical">
          <Form.Item
            label="Dispute window (days)"
            style={{ marginBottom: 0 }}
            extra="Days after completion that agents may dispute. If set to 0, agents will have no option to dispute and can only acknowledge. However, authorized users (admins, QA admins, and the assigned evaluator) can still dispute if this value is set to 0. Check 'Disable Dispute' to hide the dispute option from the agent but still allow a period to acknowledge."
          >
            <Space align="center">
              <InputNumber
                min={0}
                max={365}
                value={formState.dispute_window_days}
                onChange={(v) => setFormState((f) => ({ ...f, dispute_window_days: v ?? 4 }))}
                addonAfter="days"
                style={{ width: 180 }}
                disabled={readOnly}
              />
              <Checkbox
                checked={formState.disable_dispute}
                onChange={(e) => setFormState((f) => ({ ...f, disable_dispute: e.target.checked }))}
                disabled={readOnly}
              >
                Disable Dispute
              </Checkbox>
            </Space>
          </Form.Item>
          {formType === 'error_tracking' && (
            <Form.Item label="Error counting" style={{ marginBottom: 0, marginTop: 12 }}>
              <Checkbox
                checked={formState.max_one_error_per_section !== false}
                onChange={(e) => setFormState((f) => ({ ...f, max_one_error_per_section: e.target.checked }))}
                disabled={readOnly}
              >
                Max 1 error per section (applies to both errors and feedback)
              </Checkbox>
            </Form.Item>
          )}
        </Form>
      </Card>}

      {/* Score Thresholds — not applicable for error tracking or help desk forms */}
      {SHOW_FORM_SCORE_THRESHOLDS && formType !== 'error_tracking' && formType !== 'helpdesk' && <Card
        title="Score Thresholds"
        size="small"
        style={{ marginBottom: 16 }}
        extra={totalMaxPts > 0 && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Form total: {totalMaxPts} pts
          </Typography.Text>
        )}
      >
        <Form layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item label="Silver" style={{ marginBottom: 0 }}>
                <InputNumber
                  min={0}
                  max={totalMaxPts || undefined}
                  value={formState.score_min}
                  onChange={(v) => setFormState((f) => ({ ...f, score_min: v ?? null }))}
                  addonAfter="pts"
                  style={{ width: '100%' }}
                  disabled={readOnly}
                  placeholder="—"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Target (Gold)" style={{ marginBottom: 0 }}>
                <InputNumber
                  min={0}
                  max={totalMaxPts || undefined}
                  value={formState.score_target}
                  onChange={(v) => setFormState((f) => ({ ...f, score_target: v ?? null }))}
                  addonAfter="pts"
                  style={{ width: '100%' }}
                  disabled={readOnly}
                  placeholder="—"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Platinum" style={{ marginBottom: 0 }}>
                <InputNumber
                  min={0}
                  max={totalMaxPts || undefined}
                  value={formState.score_high}
                  onChange={(v) => setFormState((f) => ({ ...f, score_high: v ?? null }))}
                  addonAfter="pts"
                  style={{ width: '100%' }}
                  disabled={readOnly}
                  placeholder="—"
                />
              </Form.Item>
            </Col>
          </Row>


        </Form>
      </Card>}

      {/* Sections */}
      {formState.sections.map((sec, si) => (
        <SectionCard
          key={sec._key}
          section={sec}
          onUpdate={(patch) => updateSection(sec._key, patch)}
          onDelete={() => deleteSection(sec._key)}
          onMove={(dir) => moveSection(sec._key, dir)}
          isFirst={si === 0}
          isLast={si === formState.sections.length - 1}
          readOnly={readOnly}
          textEditOnly={textEditOnly}
          formType={formType}
        />
      ))}

      {!readOnly && !textEditOnly && (
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addSection}
          block
          style={{ marginBottom: 24 }}
        >
          Add Section
        </Button>
      )}

      {/* Action bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={() => navigate('/eval-forms')}>
          {readOnly || textEditOnly ? 'Back' : 'Cancel'}
        </Button>
        {textEditOnly && (
          <Button type="primary" onClick={handleSaveText} loading={savingText}>
            Save Changes
          </Button>
        )}
        {!readOnly && !textEditOnly && (
          <>
            <Button onClick={handleSave} loading={saving}>
              Save Draft
            </Button>
            <Button type="primary" onClick={handlePublish} loading={publishing}>
              Publish
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
